import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import api from "../../api/axiosConfig.js";

const ProjectBarChart = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [error, setError] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState([]);
  const [selectedLogTitle, setSelectedLogTitle] = useState([]); // Multi-select for log titles
  const [timesheetData, setTimesheetData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      const timesheetEndpoint = `/api/timesheets`;
      const nonProductiveEndpoint = `/api/nonproductive/timesheets`;
      const response = await api.get(timesheetEndpoint);
      const nonProductiveResponse = await api.get(nonProductiveEndpoint);
      const estimateEndpoint = `/api/estimate/timesheets`;
      const estimateResponse = await api.get(estimateEndpoint);
      const timesheets = [
        ...response.data,
        ...nonProductiveResponse.data,
        ...estimateResponse.data,
      ];
      setTimesheetData(timesheets);
      // Only set default if no selectedMonth is chosen yet
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTimesheets();
  }, []);

  const projectOptions = useMemo(() => {
    // Remove duplicates and sort by project_id descending
    const sortedData = [...timesheetData].sort(
      (a, b) => b.project_id - a.project_id
    );
    const uniqueProjectsMap = new Map();

    sortedData.forEach((entry) => {
      if (!uniqueProjectsMap.has(entry.project_name)) {
        uniqueProjectsMap.set(entry.project_name, entry);
      }
    });

    // Extract non-productive and other projects
    const options = [];
    const nonProductive = [];

    for (let [projectName, entry] of uniqueProjectsMap.entries()) {
      const option = {
        value: projectName,
        label: projectName,
      };

      if (entry.category === "Non Productive") {
        nonProductive.push(option);
      } else {
        options.push(option);
      }
    }

    return [
      { value: "all", label: "Select All" },
      ...nonProductive,
      ...options,
    ];
  }, [timesheetData]);

  const userOptions = useMemo(() => {
    let filteredData = timesheetData;

    if (selectedProject.length > 0) {
      const selectedProjectNames = selectedProject.map(
        (project) => project.value
      );
      filteredData = filteredData.filter((entry) =>
        selectedProjectNames.includes(entry.project_name)
      );
    }

    const uniqueUsers = [
      ...new Set(filteredData.map((entry) => entry.user_name)),
    ];
    return uniqueUsers
      .sort((a, b) => a.localeCompare(b))
      .map((user) => ({ value: user, label: user }));
  }, [timesheetData, selectedProject]);

  const logTitleOptions = useMemo(() => {
    const uniqueLogTitles = [
      ...new Set(timesheetData.map((entry) => entry.logtitle)),
    ];
    return uniqueLogTitles
      .sort((a, b) => a.localeCompare(b))
      .map((title) => ({ value: title, label: title }));
  }, [timesheetData]);
  // Handle select/deselect logic
  const handleProjectChange = (selected) => {
    if (!selected) {
      setSelectedProject([]);
      return;
    }

    const isSelectAll = selected.some((option) => option.value === "all");

    if (isSelectAll) {
      setSelectedProject(projectOptions.slice(1)); // Select everything except "Select All"
    } else {
      setSelectedProject(selected);
    }
  };
  const handleClear = () => {
    setSelectedProject([]);
    setSelectedLogTitle([]);
    setSelectedUsers([]);
    setStartDate(null);
    setEndDate(null);
  };
  const filteredData = useMemo(() => {
    let data = timesheetData;

    if (startDate && endDate) {
      data = data.filter((entry) => {
        const entryDate = new Date(entry.date);
        return entryDate >= startDate && entryDate <= endDate;
      });
    }

    if (selectedProject.length > 0) {
      const selectedProjectNames = selectedProject.map(
        (project) => project.value
      );
      data = data.filter((entry) =>
        selectedProjectNames.includes(entry.project_name)
      );
    }

    if (selectedUsers.length > 0) {
      const selectedUserNames = selectedUsers.map((user) => user.value);
      data = data.filter((entry) =>
        selectedUserNames.includes(entry.user_name)
      );
    }

    if (selectedLogTitle.length > 0) {
      const selectedLogTitles = selectedLogTitle.map((log) => log.value);
      data = data.filter((entry) => selectedLogTitles.includes(entry.logtitle));
    }

    return data;
  }, [
    timesheetData,
    startDate,
    endDate,
    selectedProject,
    selectedUsers,
    selectedLogTitle,
  ]);

  const hoursByCategory = useMemo(() => {
    const rawResult = { nonProductive: {}, productive: {} };

    filteredData.forEach(({ user_name, duration, category }) => {
      const [hours, minutes] = duration.split(":").map(Number);
      const totalHours = hours + minutes / 60;

      if (category === "Non Productive") {
        rawResult.nonProductive[user_name] =
          (rawResult.nonProductive[user_name] || 0) + totalHours;
      } else {
        rawResult.productive[user_name] =
          (rawResult.productive[user_name] || 0) + totalHours;
      }
    });

    const sortByUser = (obj) =>
      Object.fromEntries(
        Object.entries(obj).sort(([a], [b]) => a.localeCompare(b))
      );

    return {
      nonProductive: sortByUser(rawResult.nonProductive),
      productive: sortByUser(rawResult.productive),
    };
  }, [filteredData]);

  const option = useMemo(() => {
    const users = Array.from(
      new Set([
        ...Object.keys(hoursByCategory.nonProductive),
        ...Object.keys(hoursByCategory.productive),
      ])
    );

    const series = [
      {
        name: "Non Productive",
        type: "bar",
        barWidth: "40%",
        data: users.map((user) => ({
          value: Math.round(hoursByCategory.nonProductive[user]) || 0,
          itemStyle: { color: "#4BD5CE" },
          label: { show: true, position: "top", color: "#000", fontSize: 12 },
        })),
      },
      {
        name: "Productive",
        type: "bar",
        barWidth: "40%",
        data: users.map((user) => ({
          value: Math.round(hoursByCategory.productive[user]) || 0,
          itemStyle: { color: "#FEC941" },
          label: { show: true, position: "top", color: "#000", fontSize: 12 },
        })),
      },
    ];

    return {
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: {
        data: [
          { name: "Non Productive", itemStyle: { color: "#4BD5CE" } },
          { name: "Productive", itemStyle: { color: "#FEC941" } },
        ],
      },
      grid: { left: "3%", right: "4%", bottom: "8%", containLabel: true },
      xAxis: [
        {
          type: "category",
          data: users,
          name: "Users",
          nameLocation: "middle",
          nameGap: 120,
          axisLabel: { fontSize: 11, color: "#000", rotate: 90 },
        },
      ],
      yAxis: [
        {
          type: "value",
          name: "Hours",
          nameLocation: "middle",
          nameGap: 50,
          axisLabel: { fontSize: 13, color: "#000" },
        },
      ],
      series,
    };
  }, [hoursByCategory]);

  return (
    <>
      <div className="project-bar-status-report">
        <h2 className="pie-header">User's Summary</h2>
        <div className="report-user-summary-con">
          <p style={{ margin: "0px" }}>Project Name</p>
          <Select
            isMulti
            options={projectOptions}
            value={selectedProject}
            onChange={handleProjectChange}
            placeholder="Select Project"
            styles={{
              control: (base) => ({
                ...base,
                cursor: "pointer",
                width: "200px",
                minHeight: "40px",
                maxHeight: "100px", // Adjust height to avoid placeholder overflow
                overflowY: "auto", // Enable scrolling inside the dropdown
              }),
              valueContainer: (base) => ({
                ...base,
                maxHeight: "80px", // Limits the height of the selected values
                overflowY: "auto", // Allows scrolling when multiple values are selected
                scrollbarWidth: "none", // Hides scrollbar for Firefox
                msOverflowStyle: "none", // Hides scrollbar for IE/Edge
              }),
              option: (base) => ({
                ...base,
                cursor: "pointer",
              }),
            }}
          />
          <p style={{ margin: "0" }}>Select Users</p>
          <Select
            isMulti
            options={userOptions}
            value={selectedUsers}
            onChange={setSelectedUsers}
            placeholder="Select Users"
            styles={{
              control: (base) => ({
                ...base,
                cursor: "pointer",
                width: "200px",
                minHeight: "40px",
                maxHeight: "100px", // Prevents the placeholder from expanding too much
                overflowY: "auto",
              }),
              valueContainer: (base) => ({
                ...base,
                maxHeight: "80px", // Restricts selected options height
                overflowY: "auto", // Enables scrolling for selected options
                scrollbarWidth: "none", // Hides scrollbar for Firefox
                msOverflowStyle: "none", // Hides scrollbar for IE/Edge
              }),
              option: (base) => ({
                ...base,
                cursor: "pointer",
              }),
            }}
          />

          <p style={{ margin: "0" }}>Select Tasks</p>
          <Select
            isMulti
            options={logTitleOptions}
            value={selectedLogTitle}
            onChange={setSelectedLogTitle}
            placeholder="Select Log Titles"
            styles={{
              control: (base) => ({
                ...base,
                cursor: "pointer",
                width: "200px",
                minHeight: "40px",
                maxHeight: "100px", // Restricts placeholder height
                overflowY: "auto",
              }),
              valueContainer: (base) => ({
                ...base,
                maxHeight: "80px", // Enables scrolling for selected options
                overflowY: "auto",
                scrollbarWidth: "none", // Hides scrollbar for Firefox
                msOverflowStyle: "none", // Hides scrollbar for IE/Edge
              }),
              option: (base) => ({
                ...base,
                cursor: "pointer",
              }),
            }}
          />
          <DatePicker
            className="project-barchart-datepicker"
            dateFormat={"dd-MM-yyyy"}
            selected={startDate}
            onChange={setStartDate}
            placeholderText="Start Date"
          />
          <DatePicker
            className="project-barchart-datepicker"
            dateFormat={"dd-MM-yyyy"}
            selected={endDate}
            onChange={setEndDate}
            placeholderText="End Date"
          />
          <button className="chart-clear-btn" onClick={handleClear}>
            Clear
          </button>
        </div>

        <ReactECharts option={option} style={{ height: "500px" }} />
      </div>
    </>
  );
};

export default ProjectBarChart;
